import { cn } from '@/lib/cn';
import type { SlothState } from './sloth-states';

/**
 * 最後一層 fallback：連 neutral 都沒有素材時畫的柔和輪廓（spec 22.2）。
 * 刻意不畫五官、服裝與配件，避免先假設角色設定；
 * 狀態差異只用背後的柔光顏色表示。
 *
 * 正常情況下看不到這個元件——resolveSlothSequence 至少會退到 neutral 的真圖。
 * 它存在是為了讓 src/assets/sloth/ 整個空掉時畫面仍然成立。
 */
const STATE_ACCENT: Record<SlothState, string> = {
  neutral: 'var(--text-muted)',
  welcome: 'var(--accent)',
  working: 'var(--accent)',
  waiting: 'var(--warning)',
  reminder: 'var(--accent-strong)',
  stuck: 'var(--warning)',
  success: 'var(--success)',
  empty: 'var(--text-muted)',
  backup: 'var(--warning)',
  night: 'var(--accent)',
};

export interface SlothPlaceholderProps {
  state: SlothState;
  className?: string;
}

export function SlothPlaceholder({ state, className }: SlothPlaceholderProps) {
  const accent = STATE_ACCENT[state];

  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 200 250"
      className={cn('h-auto w-full', className)}
      preserveAspectRatio="xMidYMid meet"
    >
      {/* 4:5 透明畫布，與 22.3 的素材規格一致。 */}
      <circle cx="100" cy="140" r="76" fill={accent} opacity="0.12" />

      {/* 樹枝 */}
      <path d="M16 58h168" stroke="var(--border-strong)" strokeWidth="7" strokeLinecap="round" />

      {/* 手臂外框，讓淺色主題下也看得出輪廓 */}
      <path
        d="M76 112C58 104 48 84 47 62"
        stroke="var(--border-strong)"
        strokeWidth="20"
        strokeLinecap="round"
        fill="none"
        opacity="0.45"
      />
      <path
        d="M124 112c18-8 28-28 29-50"
        stroke="var(--border-strong)"
        strokeWidth="20"
        strokeLinecap="round"
        fill="none"
        opacity="0.45"
      />
      {/* 手臂掛在樹枝上 */}
      <path
        d="M76 112C58 104 48 84 47 62"
        stroke="var(--surface-raised)"
        strokeWidth="18"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M124 112c18-8 28-28 29-50"
        stroke="var(--surface-raised)"
        strokeWidth="18"
        strokeLinecap="round"
        fill="none"
      />

      {/* 身體 */}
      <ellipse
        cx="100"
        cy="164"
        rx="46"
        ry="54"
        fill="var(--surface-raised)"
        stroke="var(--border-strong)"
        strokeWidth="2"
      />
      <ellipse cx="100" cy="172" rx="28" ry="34" fill={accent} opacity="0.16" />

      {/* 耳朵與頭；不畫五官 */}
      <circle
        cx="73"
        cy="86"
        r="12"
        fill="var(--surface-raised)"
        stroke="var(--border-strong)"
        strokeWidth="2"
      />
      <circle
        cx="127"
        cy="86"
        r="12"
        fill="var(--surface-raised)"
        stroke="var(--border-strong)"
        strokeWidth="2"
      />
      <circle
        cx="100"
        cy="100"
        r="34"
        fill="var(--surface-raised)"
        stroke="var(--border-strong)"
        strokeWidth="2"
      />

      {/* 落在桌面上的柔和影子 */}
      <ellipse cx="100" cy="228" rx="44" ry="7" fill="var(--border)" opacity="0.7" />
    </svg>
  );
}
